import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StateEntityGroupsService } from '../../state-entity-groups.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-state-group-edit',
    templateUrl: './state-group-edit.component.html',
    styleUrls: ['./state-group-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StateGroupEditComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  modalTitle = "Edit Group Details";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;

  // Data properties
  rowData: any;
  gridData: any[] = [];
  filterParams: any = {};
  baseUrl: string = '';

  // Group data
  allSelectedGroups: any[] = [];
  allSelectedGroupsBackup: any[] = [];
  newGroups: any[] = [];

  // Global services
  USER_SETTINGS: any;
  GENERAL_CONFIG: any;
  globalService: any;

  constructor(
    private stateEntityService: StateEntityGroupsService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  private initCalled = false;
  private tryInit() {
    // Check if we have either rowData OR gridData along with filterParams
    const hasRowData = (this.rowData && Object.keys(this.rowData).length > 0);
    const hasGridData = (this.gridData && this.gridData.length > 0);
    const hasFilterParams = (this.filterParams && Object.keys(this.filterParams).length > 0);
    const hasHeaderData = !!(this.globalService && this.globalService.globalParams != null);
    
    const isDataReady = (hasRowData || hasGridData) && hasFilterParams && hasHeaderData;
    
    if (!this.initCalled && isDataReady) {
      this.initCalled = true;
      this.init();
    }
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.initCalled = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.initCalled = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.baseUrl = this.GENERAL_CONFIG.base_url || '';
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? (rawRow[0]?.data ?? rawRow[0] ?? {})
      : (rawRow || {});
    this.applyGridPayload(gridPayloadFromActiveScreen(activeScreen));
    this.globalService = {
      ...header,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      filterParams: this.filterParams,
    };
    this.cdr.markForCheck();
  }

  private applyGridPayload(grid: any): void {
    if (!grid) {
      this.gridData = [];
      return;
    }
    if (Array.isArray(grid) && grid.length > 0) {
      this.gridData = _.cloneDeep(grid);
    } else if (typeof grid === 'object' && grid.rowData && Object.keys(grid.rowData).length > 0) {
      this.rowData = grid.rowData;
      this.gridData = [{ data: grid.rowData }];
    } else {
      this.gridData = [];
    }
  }

  init() {
    try {
      // Populate allSelectedGroups from gridData
      this.allSelectedGroups = [];
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.allSelectedGroups.push(this.rowData);
      } else if (this.gridData.length > 0) {
        this.gridData.forEach((row: any) => {
          this.allSelectedGroups.push(row.data);
        });
      } else {
        this._snackBar.open('No group is selected for editing', 'Close', {
          duration: 4000,
          panelClass: ['info-snackbar']
        });
      }

      this.allSelectedGroupsBackup = _.cloneDeep(this.allSelectedGroups);
      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error in state group edit init:', e);
    }
  }

  addGroup() {
    const tempModelObject = {
      "CONSOL_TYPE": "SINGLE",
      "CREATED_BY": this.USER_SETTINGS?.user?.sso_id || "",
      "CREATED_ON": "",
      "FILER_TAX_YEAR_BEGIN": "",
      "FILER_TAX_YEAR_END": "",
      "GROUP_FLAG": "State",
      "GROUP_NAME": "",
      "GROUP_OBJ_KEY": "",
      "GROUP_TYPE": "REG",
      "LOCK_Y_N": "N",
      "MEF_GROUP_KEY": "",
      "PARENT_KEY": "",
      "SCENARIO": "",
      "STATE": "",
      "TAX_YEAR": "",
      "UPDATED_BY": this.USER_SETTINGS?.user?.sso_id || "",
      "UPDATED_ON": ""
    };
    this.newGroups.push(tempModelObject);
    this.cdr.detectChanges();
  }

  removeGroup(index: number) {
    if (this.newGroups.length > 0) {
      this.newGroups.splice(index, 1);
      this.cdr.detectChanges();
    }
  }

  validation(): boolean {
    // Check existing groups for empty names
    for (let i = 0; i < this.allSelectedGroups.length; i++) {
      if (!this.allSelectedGroups[i].GROUP_NAME || this.allSelectedGroups[i].GROUP_NAME.trim() === "") {
        return false;
      }
    }

    // Check new groups for empty names
    for (let i = 0; i < this.newGroups.length; i++) {
      if (!this.newGroups[i].GROUP_NAME || this.newGroups[i].GROUP_NAME.trim() === "") {
        return false;
      }
    }

    return true;
  }

  save() {
    if (!this.validation()) {
      this._snackBar.open('Group name can not be empty.', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    // Combine existing and new groups
    const allData = this.allSelectedGroups.concat(this.newGroups);

    // Get rowData for settings
    const firstGroup = this.allSelectedGroups[0] || {};

    this.stateEntityService.updateStateGroup(
      this.baseUrl,
      allData,
      this.globalService,
      this.filterParams,
      firstGroup,
      this.USER_SETTINGS
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.callSuccess == "1" && response.errorMessage == "null") {
          this._snackBar.open('Groups Updated successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // Emit refresh event
          this.requestGridUpdate.emit('refresh');
        } else {
          if (response.errorMessage && response.errorMessage !== 'null') {
            this._snackBar.open(response.errorMessage, 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          } else {
            this._snackBar.open('Error occurred in updating the groups. If this continues please contact support', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        console.error('Error updating state groups:', error);
        this._snackBar.open('Error occurred while updating groups. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  reset() {
    this.allSelectedGroups = _.cloneDeep(this.allSelectedGroupsBackup);
    this.newGroups = [];
    this.cdr.detectChanges();
  }

  cancel() {
    // Reset data before closing (match AngularJS behavior)
    this.reset();
    this.requestGridUpdate.emit('cancel');
  }
}
